import React, { useState, useEffect, useRef } from 'react';
import { Event } from './types';
import { format } from 'date-fns';
import { edgeFunctionUrl, EdgeFunctions } from '../../../lib/edgeFunctions';
import { supabase } from '../../../lib/supabaseClient';
import { loadStripeTerminal } from '@stripe/terminal-js';
import TipModal from './TipModal';

interface AppointmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Event | null;
  onEdit?: () => void;
  onDelete?: (eventId: string | number) => Promise<void>;
}

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  isOpen,
  onClose,
  appointment,
  onEdit,
  onDelete
}) => {
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Terminal payment states
  const [isProcessingTerminal, setIsProcessingTerminal] = useState(false);
  const [terminalError, setTerminalError] = useState<string | null>(null);
  const [terminalStatus, setTerminalStatus] = useState<string>('');
  const [canCancelPayment, setCanCancelPayment] = useState(false);
  const terminalRef = useRef<any>(null);
  const discoveredReadersRef = useRef<any[]>([]);
  const paymentIntentRef = useRef<any>(null);

  // Tip states
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const [existingTip, setExistingTip] = useState<{ amount: number; tip_percentage: number | null } | null>(null);
  const [isFetchingTip, setIsFetchingTip] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [paymentSource, setPaymentSource] = useState<string | null>(null);

  // Initialize Stripe Terminal
  useEffect(() => {
    const initializeTerminal = async () => {
      if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
        console.error('Stripe publishable key not found');
        return;
      }

      try {
        const StripeTerminal = await loadStripeTerminal();

        if (!StripeTerminal) {
          console.error('Failed to load Stripe Terminal');
          return;
        }

        terminalRef.current = StripeTerminal.create({
          onFetchConnectionToken: async () => {
            // Fetch connection token from our edge function
            const { data: { session } } = await supabase.auth.getSession();

            const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_CONNECTION_TOKEN), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session?.access_token || ''}`,
                'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
              },
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || 'Failed to fetch connection token');
            }

            return data.secret;
          },
          onUnexpectedReaderDisconnect: () => {
            setTerminalStatus('Reader disconnected');
          },
        });
      } catch (error) {
        // Terminal initialization failed
      }
    };

    if (isOpen) {
      initializeTerminal();
    }

    return () => {
      // Cleanup: safely disconnect reader when modal closes
      const cleanup = async () => {
        if (terminalRef.current) {
          try {
            // Only disconnect if we're actually connected
            const status = terminalRef.current.getConnectionStatus();
            if (status === 'connected') {
              await terminalRef.current.disconnectReader();
            }
          } catch (err) {
            // Silent cleanup
          }
        }
      };
      cleanup();
    };
  }, [isOpen]);

  // Fetch existing tip and payment method when appointment changes
  useEffect(() => {
    const fetchTipAndPaymentInfo = async () => {
      if (!appointment || !appointment.id) {
        setExistingTip(null);
        setPaymentMethodId(null);
        setPaymentSource(null);
        return;
      }

      setIsFetchingTip(true);
      try {
        // Fetch tip
        const { data: tipData, error: tipError } = await supabase
          .from('tips')
          .select('amount, tip_percentage')
          .eq('appointment_id', appointment.id)
          .eq('status', 'succeeded')
          .single();

        if (tipError) {
          setExistingTip(null);
        } else {
          setExistingTip(tipData);
        }

        // Fetch payment method ID and source for restaurant-style tips
        const { data: paymentData, error: paymentError } = await supabase
          .from('payments')
          .select('payment_method_id, payment_source')
          .eq('appointment_id', appointment.id)
          .eq('status', 'succeeded')
          .single();

        if (paymentError) {
          setPaymentMethodId(null);
          setPaymentSource(null);
        } else {
          setPaymentMethodId(paymentData?.payment_method_id || null);
          setPaymentSource(paymentData?.payment_source || null);
        }
      } catch (err) {
        setExistingTip(null);
        setPaymentMethodId(null);
        setPaymentSource(null);
      } finally {
        setIsFetchingTip(false);
      }
    };

    fetchTipAndPaymentInfo();
  }, [appointment?.id]);

  if (!isOpen || !appointment || appointment.isBlockedTime) return null;

  const handleCreatePaymentLink = async () => {
    setIsCreatingPayment(true);
    setPaymentError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      // Determine redirect path based on current location (admin vs crew)
      const currentPath = window.location.pathname;
      const redirectPath = currentPath.includes('/crew/') ? '/crew/schedule' : '/admin/schedule';

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_CHECKOUT_SESSION), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointment.id,
          return_url: window.location.origin,
          redirect_path: redirectPath, // Tell edge function where to redirect after payment
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment link');
      }

      // Open the checkout URL in the same window (for tablet use)
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error: any) {
      setPaymentError(error.message || 'Failed to create payment link');
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handleCancelPayment = async () => {
    try {
      setTerminalStatus('Cancelling...');

      // Cancel the payment intent if it exists
      if (paymentIntentRef.current) {
        await terminalRef.current?.cancelCollectPaymentMethod();
      }

      // Disconnect reader
      if (terminalRef.current) {
        const status = terminalRef.current.getConnectionStatus();
        if (status === 'connected') {
          await terminalRef.current.disconnectReader();
        }
      }

      // Reset states
      setTerminalStatus('');
      setTerminalError(null);
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
      paymentIntentRef.current = null;

    } catch (error: any) {
      setTerminalError('Failed to cancel payment. Please try closing the modal.');
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
    }
  };

  const handleProcessOnReader = async () => {
    setIsProcessingTerminal(true);
    setTerminalError(null);
    setTerminalStatus('Connecting to reader...');
    setCanCancelPayment(false);

    try {
      if (!terminalRef.current) {
        throw new Error('Terminal not initialized');
      }

      if (!process.env.REACT_APP_STRIPE_LOCATION_ID) {
        throw new Error('Stripe Location ID not configured. Please add REACT_APP_STRIPE_LOCATION_ID to your .env file');
      }

      // Step 1: Discover readers
      setTerminalStatus('Discovering readers...');

      // Use simulated reader in test mode for development
      const isTestMode = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.includes('test') || false;
      const discoverConfig = isTestMode
        ? { simulated: true }  // Simulated reader for testing
        : { location: process.env.REACT_APP_STRIPE_LOCATION_ID };  // Real reader for production

      const discoverResult = await terminalRef.current.discoverReaders(discoverConfig);

      if (discoverResult.error) {
        throw new Error(`Failed to discover readers: ${discoverResult.error.message}`);
      }

      if (discoverResult.discoveredReaders.length === 0) {
        const errorMsg = isTestMode
          ? 'No simulated readers found. This might be a Stripe SDK issue - try refreshing the page.'
          : 'No readers found. Make sure your S700 is online and registered.';
        throw new Error(errorMsg);
      }

      discoveredReadersRef.current = discoverResult.discoveredReaders;

      // Step 2: Connect to first available reader
      const readerLabel = discoveredReadersRef.current[0].label || 'reader';
      const readerType = isTestMode ? ' (simulated)' : '';
      setTerminalStatus(`Connecting to ${readerLabel}${readerType}...`);
      const connectResult = await terminalRef.current.connectReader(discoveredReadersRef.current[0]);

      if (connectResult.error) {
        throw new Error(`Failed to connect to reader: ${connectResult.error.message}`);
      }

      // Step 3: Create Terminal PaymentIntent via edge function
      setTerminalStatus('Creating payment intent...');
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(edgeFunctionUrl(EdgeFunctions.CREATE_TERMINAL_PAYMENT_INTENT), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          appointment_id: appointment.id,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create payment intent');
      }

      paymentIntentRef.current = data.clientSecret;

      // Step 4: Collect payment on reader
      setTerminalStatus('Present card on reader...');
      setCanCancelPayment(true); // Allow cancellation while waiting for card

      const collectResult = await terminalRef.current.collectPaymentMethod(data.clientSecret);

      if (collectResult.error) {
        // Check if it was manually cancelled
        if (collectResult.error.code === 'canceled') {
          throw new Error('Payment cancelled by user');
        }
        throw new Error(`Payment collection failed: ${collectResult.error.message}`);
      }

      setCanCancelPayment(false); // No longer cancellable once card is collected

      // Step 5: Process payment
      setTerminalStatus('Processing payment...');
      const processResult = await terminalRef.current.processPayment(collectResult.paymentIntent);

      if (processResult.error) {
        throw new Error(`Payment processing failed: ${processResult.error.message}`);
      }

      // Extract payment method ID for restaurant-style tips
      const paymentMethodId = processResult.paymentIntent.payment_method || null;

      // Step 6: Update appointment status to paid
      const { error: updateError } = await supabase
        .from('appointments')
        .update({
          payment_status: 'paid',
          payment_method_id: paymentMethodId,
        })
        .eq('id', appointment.id);

      if (updateError) {
        throw new Error('Payment successful but failed to update appointment');
      }

      // Update payment record with payment method and source
      const { error: paymentUpdateError } = await supabase
        .from('payments')
        .update({
          status: 'succeeded',
          payment_method_id: paymentMethodId,
          payment_source: 'terminal',
        })
        .eq('stripe_payment_intent_id', processResult.paymentIntent.id);

      if (paymentUpdateError) {
        // Don't throw - appointment is already marked paid
      }

      setTerminalStatus('Payment successful! ✓');

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        window.location.reload(); // Refresh to show updated payment status
      }, 2000);

    } catch (error: any) {
      setTerminalError(error.message || 'Failed to process payment on reader');
      setTerminalStatus('');

      // Disconnect reader on error
      try {
        if (terminalRef.current) {
          const status = terminalRef.current.getConnectionStatus();
          if (status === 'connected') {
            await terminalRef.current.disconnectReader();
          }
        }
      } catch (disconnectErr) {
        // Silent cleanup
      }
    } finally {
      setIsProcessingTerminal(false);
      setCanCancelPayment(false);
      paymentIntentRef.current = null;
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to cancel this appointment?')) {
      if (onDelete && appointment.id) {
        await onDelete(appointment.id);
        onClose();
      }
    }
  };

  const handleTipSuccess = (tipData: { amount: number; tip_percentage: number | null }) => {
    // Update tip state immediately with the data from TipModal
    setExistingTip(tipData);
  };

  const getPaymentStatusBadge = (status?: string) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Paid</span>;
      case 'pending_payment':
      case 'pending': // Handle legacy 'pending' value
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">Pending Payment</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">Failed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded">Unknown</span>;
    }
  };

  const getSourceBadge = (source?: string) => {
    switch (source) {
      case 'online':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">Online Booking</span>;
      case 'admin':
        return <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded">Admin Created</span>;
      case 'phone':
        return <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded">Phone Booking</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded">Unknown</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Appointment Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {/* Client Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Client Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Name:</span>
                <span className="text-gray-900">{appointment.client || 'N/A'}</span>
              </div>
              {appointment.clientEmail && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Email:</span>
                  <span className="text-gray-900">{appointment.clientEmail}</span>
                </div>
              )}
              {appointment.clientPhone && (
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Phone:</span>
                  <span className="text-gray-900">{appointment.clientPhone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Services</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              {appointment.services && appointment.services.length > 0 ? (
                <div className="space-y-3">
                  {appointment.services.map((service, index) => (
                    <div key={index} className="flex justify-between items-center pb-3 border-b border-gray-200 last:border-b-0 last:pb-0">
                      <div>
                        <div className="font-medium text-gray-900">{service.name}</div>
                        <div className="text-sm text-gray-500">{service.duration} minutes</div>
                      </div>
                      <div className="text-gray-900 font-semibold">
                        ${service.price.toFixed(2)}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-3 border-t-2 border-gray-300">
                    <div className="font-semibold text-gray-900">Total</div>
                    <div className="text-lg font-bold text-gray-900">
                      ${(appointment.totalAmount || 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-500">No services listed</div>
              )}
            </div>
          </div>

          {/* Appointment Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Appointment Details</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Date:</span>
                <span className="text-gray-900">{format(new Date(appointment.start), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Time:</span>
                <span className="text-gray-900">
                  {format(new Date(appointment.start), 'h:mm a')} - {format(new Date(appointment.end), 'h:mm a')}
                </span>
              </div>
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Status:</span>
                <span className={`px-2 py-1 text-xs font-semibold rounded ${
                  appointment.status === 'scheduled' ? 'bg-green-100 text-green-800' :
                  appointment.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                  appointment.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                  appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  appointment.status === 'no_show' ? 'bg-gray-100 text-gray-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {appointment.status?.replace('_', ' ').split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-3">Payment Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Payment Status:</span>
                {getPaymentStatusBadge(appointment.paymentStatus)}
              </div>
              <div className="flex">
                <span className="w-32 text-gray-600 font-medium">Total Amount:</span>
                <span className="text-gray-900 font-semibold">${(appointment.totalAmount || 0).toFixed(2)}</span>
              </div>

              {/* Tip Display - Show if tip exists */}
              {existingTip && (
                <div className="flex items-center">
                  <span className="w-32 text-gray-600 font-medium">Tip:</span>
                  <span className="text-green-700 font-semibold">
                    ${existingTip.amount.toFixed(2)}
                    {existingTip.tip_percentage && (
                      <span className="text-sm text-gray-500 ml-1">({existingTip.tip_percentage}%)</span>
                    )}
                  </span>
                </div>
              )}

              {/* Add Tip Button - Show when paid and no existing tip */}
              {appointment.paymentStatus === 'paid' && !existingTip && !isFetchingTip && (
                <div className="pt-2 border-t border-gray-200">
                  <button
                    onClick={() => setIsTipModalOpen(true)}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Add Tip</span>
                  </button>
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    Add a tip for the service provider
                  </p>
                </div>
              )}

              {/* Payment Buttons - Only show if payment is not paid */}
              {appointment.paymentStatus !== 'paid' && (
                <div className="pt-2 border-t border-gray-200 space-y-3">
                  {/* Terminal Status Display */}
                  {terminalStatus && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800 text-center">{terminalStatus}</p>
                    </div>
                  )}

                  {/* Terminal Error Display */}
                  {terminalError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{terminalError}</p>
                    </div>
                  )}

                  {/* Payment Link Button */}
                  <div>
                    <button
                      onClick={handleCreatePaymentLink}
                      disabled={isCreatingPayment || isProcessingTerminal}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isCreatingPayment ? 'Creating Payment Link...' : 'Open Payment Page'}
                    </button>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Opens Stripe checkout for in-person contactless payment
                    </p>
                    {paymentError && (
                      <p className="text-xs text-red-600 mt-2">{paymentError}</p>
                    )}
                  </div>

                  {/* OR Divider */}
                  <div className="flex items-center">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-sm text-gray-500">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>

                  {/* Process on Reader Button */}
                  <div>
                    {canCancelPayment ? (
                      <button
                        onClick={handleCancelPayment}
                        className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                      >
                        ✕ Cancel Payment
                      </button>
                    ) : (
                      <button
                        onClick={handleProcessOnReader}
                        disabled={isProcessingTerminal || isCreatingPayment}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isProcessingTerminal ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Processing...
                          </>
                        ) : (
                          <>
                            💳 Process Payment on Reader
                          </>
                        )}
                      </button>
                    )}
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {canCancelPayment
                        ? 'Click cancel to stop the payment process'
                        : 'Use Stripe S700 card reader for in-person payment'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Source */}
          {appointment.appointmentSource && (
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-3">Booking Information</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex">
                  <span className="w-32 text-gray-600 font-medium">Source:</span>
                  {getSourceBadge(appointment.appointmentSource)}
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {appointment.notes && (
            <div>
              <h3 className="text-lg font-medium text-gray-800 mb-3">Notes</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-900 whitespace-pre-wrap">{appointment.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-between">
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Cancel Appointment
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
            {onEdit && (
              <button
                onClick={onEdit}
                className="px-4 py-2 bg-[#3A5E63] text-white rounded hover:bg-[#2d484c] transition-colors"
              >
                Edit Appointment
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tip Modal */}
      {appointment && (
        <TipModal
          isOpen={isTipModalOpen}
          onClose={() => setIsTipModalOpen(false)}
          appointmentId={appointment.id as string}
          serviceTotal={appointment.totalAmount || 0}
          staffName={appointment.client || 'Staff Member'}
          onSuccess={handleTipSuccess}
          paymentMethodId={paymentMethodId}
          paymentSource={paymentSource}
        />
      )}
    </div>
  );
};

export default AppointmentDetailsModal;
